const content = `<p>The Template method design pattern allows to write “fill-in-the-gaps” code. A main body of code has customisation points that use <a href="/polymorphism">Polymorphism</a> to be filled by any code that implements an interface.</p>

<p>The classes that provides code for the customisation points are guaranteed that the template method takes care of rest.</p>

<p>Template method is often used with inheritance.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate. A framework allows to declare objects to be saved in a database. The classes to instantiate these objects have to inherit from the <code>DbRecord</code> class, which is provided by the framework. For example, the <code>User</code> class represents the data of a user that can be saved in database:</p>

<pre><code class="language-ruby">class User &lt; DbRecord
  attr_accessor :first_name, :last_name, :title
end</code></pre>

<p><code>DbRecord</code> has a <code>save</code> method but, before saving the object, the framework needs to ensure the object is valid:</p>

<pre><code class="language-ruby">class DbRecord
  def save
    if is_valid?
      writeObjectToDb
    else
      raise "Invalid object, cannot be saved"
    end
  end
end</code></pre>

<p>The <code>DbRecord</code> class doesn’t know how to determine if an object is valid. It lets the derived class decide:</p>

<pre><code class="language-ruby">class User &lt; DbRecord
  attr_accessor :first_name, :last_name, :title
  
  def is_valid?
    !@first_name.nil? &amp;&amp; !last_name.nil?
  end
end</code></pre>

<p>First name and last name are mandatory but the title is optional. A user can be created and saved this way:</p>

<pre><code class="language-ruby">user = User.new
user.first_name = 'John'
user.last_name = 'Doe'
user.save # calls the save method in DbRecord</code></pre>

<p>The <code>save</code> method in <code>DbRecord</code> uses the Template method pattern. It contains a main body of code that saves an object, and has the <code>is_valid?</code> customization point that is implemented by <code>User</code>. <code>User</code> only has to implement <code>is_valid?</code> and relies on <code>DbRecord</code> for the rest of the saving.</p>

<p>The polymorphism introduced by <code>is_valid?</code> (any class that implements it can work with the <code>save</code> method) achieves <a href="/low-coupling">Low coupling</a> in this code. <code>DbRecord</code> doesn’t know anything about <code>User</code> and <code>User</code> only knows that <code>DbRecord</code> needs a <code>is_valid?</code> function. If anything else changes in either of the two classes, nothing has to change in the other.</p>

<p>(Note that, even though this example is convenient to illustrate the mechanics of the Template method pattern, it does not represent the complexity of persistence frameworks! For example the Rails framework offers a DSL to express validations, produce more useful error messages and checks for validity as soon as the instantiation of the class and doesn’t wait before its saving. But that’s beside our point.)</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Template method allows to write fill-in-the-gaps code</li><li>It offers customization points to be filled by implementations</li><li>It relies on polymorphism to have low coupling with the code that fills the customization points</li></ul>
</div>`

export default content
